import { supabase } from '../lib/supabase'
import type { Deliverable, Project } from '../types/portal'

/**
 * Project and deliverable writes.
 *
 * Everything runs through the authenticated staff session and the project's
 * existing RLS policies, with the publishable key only. A refused write is
 * reported with the table and the exact Postgres message rather than being
 * swallowed — nothing silently falls back to browser-only storage.
 */

export interface WriteResult {
  ok: boolean
  error?: string
}

function fail(table: string, error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `${table}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] write refused — ${detail}`)
  return { ok: false, error: detail }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Postgres "column does not exist". */
const UNDEFINED_COLUMN = '42703'

/**
 * The database's own project id. A project created moments ago may still be
 * held under a temporary local id, so the slug is used to recover the real
 * UUID rather than writing a foreign key that cannot exist.
 */
export async function resolveProjectId(projectId: string, slug: string): Promise<string | null> {
  if (UUID.test(projectId)) return projectId
  if (!supabase || !slug) return null

  const { data, error } = await supabase
    .from('projects')
    .select('id')
    .eq('slug', slug)
    .maybeSingle()

  if (error) {
    fail('projects', error)
    return null
  }
  return data ? String(data.id) : null
}

/** The deliverable columns, mapped from the interface's own field names. */
function deliverableRow(projectId: string, item: Deliverable, sortOrder: number) {
  return {
    project_id: projectId,
    deliverable_key: item.id,
    title: item.name,
    version: item.version,
    category: item.type,
    status: item.status,
    visible_to_client: item.visibleToClient,
    sort_order: sortOrder,
  }
}

export interface CreatedProject {
  id: string
  accessToken: string
}

/**
 * Inserts the project, reads back its generated id and access token, then
 * inserts any deliverables created with it against that real project id.
 */
export async function insertProject(
  project: Project,
): Promise<{ result: WriteResult; created?: CreatedProject }> {
  if (!supabase) return { result: { ok: false, error: 'Supabase is not configured.' } }

  const { data, error } = await supabase
    .from('projects')
    .insert({
      client_name: project.client,
      project_name: project.name,
      slug: project.slug,
      status: 'active',
    })
    .select('id, access_token')
    .single()

  const result = fail('projects', error)
  if (!result.ok || !data) return { result }

  const created: CreatedProject = {
    id: String(data.id),
    accessToken: String(data.access_token ?? ''),
  }

  /* Every deliverable created with the project is stored against the real id. */
  if (project.deliverables.length > 0) {
    const { error: deliverableError } = await supabase
      .from('deliverables')
      .insert(project.deliverables.map((item, index) => deliverableRow(created.id, item, index)))
    const deliverableResult = fail('deliverables', deliverableError)
    if (!deliverableResult.ok) return { result: deliverableResult, created }
  }

  return { result: { ok: true }, created }
}

/** Project-level fields the schema carries. */
export async function updateProjectRow(
  projectId: string,
  patch: Partial<Project>,
): Promise<WriteResult> {
  if (!supabase || !UUID.test(projectId)) return { ok: true }

  const row: Record<string, unknown> = {}
  if (patch.client !== undefined) row.client_name = patch.client
  if (patch.name !== undefined) row.project_name = patch.name
  if (patch.archived !== undefined) row.status = patch.archived ? 'archived' : 'active'

  /* The shoot is project-level information and belongs on the project row, so
     the client portal reads exactly what staff saved. */
  const schedule: Record<string, unknown> = {}
  if (patch.nextShootDate !== undefined) schedule.next_shoot_date = patch.nextShootDate || null
  if (patch.nextShootTime !== undefined) schedule.next_shoot_time = patch.nextShootTime || null
  if (patch.location !== undefined) schedule.shoot_location = patch.location || null

  if (Object.keys(row).length === 0 && Object.keys(schedule).length === 0) return { ok: true }

  const { error } = await supabase
    .from('projects')
    .update({ ...row, ...schedule })
    .eq('id', projectId)

  /* If the schedule columns do not exist yet, save what can be saved and say
     plainly that the schedule was not stored — never report a false success. */
  if (error?.code === UNDEFINED_COLUMN && Object.keys(schedule).length > 0) {
    if (Object.keys(row).length > 0) {
      await supabase.from('projects').update(row).eq('id', projectId)
    }
    const detail =
      'projects: the shoot columns are missing (next_shoot_date, next_shoot_time, shoot_location)'
    console.error(`[supabase] write refused — ${detail}`, error)
    return { ok: false, error: detail }
  }

  return fail('projects', error)
}

const DELETE_ERROR = 'Project could not be deleted. Nothing was removed.'

/**
 * Permanently removes one project through a single secure database operation.
 *
 * The work is done by the `delete_project` RPC, which runs as one transaction:
 * it verifies the caller is authorised Portal staff, deletes only the rows
 * belonging to that exact project id (using the schema's own cascades where
 * they exist), and then the project row itself. If any part fails the whole
 * thing is rolled back, so a project can never be left half-deleted — which is
 * why the browser performs no deletes of its own. Deleting the project row
 * takes its access token with it, so the client link stops working.
 *
 * Nothing in Google Drive is touched.
 */
export async function deleteProjectRow(
  projectId: string,
  slug: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) {
    console.error('[supabase] delete_project not called — no database id for this project')
    return { ok: false, error: DELETE_ERROR }
  }

  /* The RPC is the ONLY permanent-delete path. There is deliberately no
     table-level delete and no fallback: a refusal must leave everything. */
  const { error } = await supabase.rpc('delete_project', { p_project_id: realId })
  if (error) {
    // The real cause is logged for debugging; staff see one plain sentence.
    console.error('[supabase] delete_project failed', error)
    return { ok: false, error: DELETE_ERROR }
  }
  return { ok: true }
}

export async function insertDeliverable(
  projectId: string,
  slug: string,
  item: Deliverable,
  sortOrder: number,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) {
    const detail = 'deliverables: the project could not be found in the database'
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }

  const { error } = await supabase
    .from('deliverables')
    .insert(deliverableRow(realId, item, sortOrder))
  return fail('deliverables', error)
}

export async function updateDeliverableRow(
  projectId: string,
  deliverableKey: string,
  patch: Partial<Deliverable>,
): Promise<WriteResult> {
  if (!supabase || !UUID.test(projectId)) return { ok: true }

  const row: Record<string, unknown> = {}
  if (patch.name !== undefined) row.title = patch.name
  if (patch.version !== undefined) row.version = patch.version
  if (patch.type !== undefined) row.category = patch.type
  if (patch.status !== undefined) row.status = patch.status
  if (patch.visibleToClient !== undefined) row.visible_to_client = patch.visibleToClient
  if (Object.keys(row).length === 0) return { ok: true }

  const { error } = await supabase
    .from('deliverables')
    .update(row)
    .eq('project_id', projectId)
    .eq('deliverable_key', deliverableKey)
  return fail('deliverables', error)
}

export async function deleteDeliverableRow(
  projectId: string,
  deliverableKey: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase
    .from('deliverables')
    .delete()
    .eq('project_id', projectId)
    .eq('deliverable_key', deliverableKey)
  return fail('deliverables', error)
}

/** Persists the studio's chosen order so the client sees the same sequence. */
export async function saveDeliverableOrderRows(
  projectId: string,
  keys: string[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  for (const [index, key] of keys.entries()) {
    const { error } = await supabase
      .from('deliverables')
      .update({ sort_order: index })
      .eq('project_id', projectId)
      .eq('deliverable_key', key)
    const result = fail('deliverables', error)
    if (!result.ok) return result
  }
  return { ok: true }
}
